import * as React from "react";
import {
  Button,
  FlexRow,
  H2,
  LoadingSpinner,
  MasonryGrid,
  Stack,
  Text,
} from "atoms";
import { useGallery } from "../api/useGallery";
import type { DeleteItemFunction, GalleryItem } from "../types";
import { GalleryItemCard } from "./GalleryItemCard";

type GalleryAllItemsListProps = {
  artistId: string;
  isEditing?: boolean;
  addItems?: VoidFunction;
  deleteItem?: DeleteItemFunction;
};

function GalleryAllItemsList({
  artistId,
  addItems = noop,
  deleteItem = noop,
  isEditing,
}: GalleryAllItemsListProps) {
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useGallery({
      artistIds: [artistId],
      perPage: 12,
      itemType: "item",
    });

  const items = React.useMemo(
    () => data?.pages?.flatMap((page) => page.data) ?? [],
    [data]
  );

  if (isLoading || !items) {
    return <LoadingSpinner />;
  }

  const loadMoreGalleryItems = () => fetchNextPage();

  const numItems = data?.pages?.[0]?.totalResults ?? 0;

  const createDeleteHandler =
    ({ id, itemType }: GalleryItem) =>
    () =>
      deleteItem({ itemId: id, itemType });

  return (
    <>
      <Stack gap="sm">
        <FlexRow alignItems="baseline">
          <Stack gap="0">
            <H2 size="h3" style={{ margin: 0 }}>
              All Work
            </H2>
            <Text as="small" size="xxs">
              {numItems} item{numItems !== 1 ? "s" : ""}
            </Text>
          </Stack>

          {isEditing && (
            <Button
              size="xs"
              variant="secondary"
              onClick={addItems}
              aria-label="Add New Item to Portfolio"
            >
              Add Items
            </Button>
          )}
        </FlexRow>

        <MasonryGrid
          items={items}
          renderItem={(item) => (
            <GalleryItemCard
              key={item.id}
              {...item}
              isEditing={isEditing}
              deleteItem={createDeleteHandler(item)}
            />
          )}
        />
      </Stack>
      <FlexRow justifyContent="center" gap="20px">
        {hasNextPage && (
          <Button
            onClick={loadMoreGalleryItems}
            variant="secondary"
            size="xs"
            disabled={isFetchingNextPage}
          >
            Load More...
          </Button>
        )}
      </FlexRow>
    </>
  );
}

export { GalleryAllItemsList };
